import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import useSiteMetadata from './hooks/useSiteMetadata';
import './i18n'; // Initialize i18n
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import FloorViewer from './pages/FloorViewer';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import UserManagement from './pages/admin/UserManagement';
import CategoryManagement from './pages/admin/CategoryManagement';
import ProjectPermissions from './pages/admin/ProjectPermissions';
import UserRoleManagement from './pages/admin/UserRoleManagement';
import SiteSettings from './pages/admin/SiteSettings';
import Layout from './components/Layout';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

function App() {
  // Apply site metadata (favicon, description, title)
  useSiteMetadata();
  
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AuthProvider>
        <PermissionProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
            <Route path="/projects" element={<PrivateRoute><Layout><Projects /></Layout></PrivateRoute>} />
            <Route path="/projects/:projectId" element={<PrivateRoute><Layout><ProjectDetail /></Layout></PrivateRoute>} />
            <Route path="/projects/:projectId/floors/:floorId" element={<PrivateRoute><Layout><FloorViewer /></Layout></PrivateRoute>} />
            <Route path="/tickets" element={<PrivateRoute><Layout><Tickets /></Layout></PrivateRoute>} />
            <Route path="/tickets/:ticketId" element={<PrivateRoute><Layout><TicketDetail /></Layout></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Layout><Profile /></Layout></PrivateRoute>} />
            <Route path="/notifications" element={<PrivateRoute><Layout><Notifications /></Layout></PrivateRoute>} />
            <Route path="/admin/users" element={<PrivateRoute><Layout><UserManagement /></Layout></PrivateRoute>} />
            <Route path="/admin/categories" element={<PrivateRoute><Layout><CategoryManagement /></Layout></PrivateRoute>} />
            <Route path="/admin/project-permissions" element={<PrivateRoute><Layout><ProjectPermissions /></Layout></PrivateRoute>} />
            <Route path="/admin/user-roles" element={<PrivateRoute><Layout><UserRoleManagement /></Layout></PrivateRoute>} />
            <Route path="/admin/site-settings" element={<PrivateRoute><Layout><SiteSettings /></Layout></PrivateRoute>} />
          </Routes>
          <Toaster />
        </BrowserRouter>
        </PermissionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;